<template>
  <div class="schedule-container">
    <h1 class="page-title">{{$t("Dars jadvali")}}</h1>

    <!-- Filters Bar (modern, compact, unobtrusive) -->
    <div class="filters-bar">
      <!-- Group filter (server-side) -->
      <div class="filter-item">
        <label for="groupFilter">{{$t("Group code")}}</label>
        <input
          id="groupFilter"
          v-model.trim="filters.groupCode"
          type="text"
          placeholder="e.g. 123-AB"
          @keydown.enter.prevent="fetchLessons"
        />
      </div>

      <!-- Day -->
      <div class="filter-item">
        <label for="dayFilter">{{$t("Day")}}</label>
        <v-select
          id="dayFilter"
          :items="days"
          v-model="filters.day"
          :clearable="true"
          hide-details
          dense
          outlined
          :placeholder="$t('Any')"
        />
      </div>

      <!-- Time -->
      <div class="filter-item">
        <label for="timeFilter">{{$t("Time")}}</label>
        <v-select
          id="timeFilter"
          :items="timesAll"
          v-model="filters.time"
          :clearable="true"
          hide-details
          dense
          outlined
          :placeholder="$t('Any')"
        />
      </div>

      <!-- Language -->
      <div class="filter-item">
        <label for="langFilter">{{$t("Language")}}</label>
        <v-select
          id="langFilter"
          :items="languageItems"
          item-text="text"
          item-value="value"
          v-model="filters.language"
          :clearable="true"
          hide-details
          dense
          outlined
          :placeholder="$t('Any')"
        />
      </div>

      <!-- Lesson Type -->
      <div class="filter-item">
        <label for="typeFilter">{{$t("Lesson Type")}}</label>
        <v-select
          id="typeFilter"
          :items="lessonTypeItems"
          item-text="text"
          item-value="value"
          v-model="filters.lessonType"
          :clearable="true"
          hide-details
          dense
          outlined
          :placeholder="$t('Any')"
        />
      </div>

      <!-- Migalka -->
      <div class="filter-item">
        <label for="migalkaFilter">{{$t("Migalka")}}</label>
        <v-select
          id="migalkaFilter"
          :items="migalkaItems"
          item-text="text"
          item-value="value"
          v-model="filters.migalka"
          :clearable="true"
          hide-details
          dense
          outlined
          :placeholder="$t('Any')"
        />
      </div>

      <!-- Actions -->
      <div class="filter-actions">
        <button class="btn" :disabled="loading" @click="fetchLessons">{{$t("Apply search")}}</button>
        <button class="btn ghost" :disabled="loading && !hasAnyFilter" @click="resetFilters">{{$t("Reset")}}</button>
      </div>
    </div>

    <div v-if="loading" class="loader">{{$t("Loading schedule...")}}</div>
    <div v-else-if="!filteredLessons.length" class="no-data">{{ $t("No lessons available.") }}</div>

    <table v-else class="schedule-table">
      <thead>
        <tr class="time-cell">
          <th class="time-header">{{$t("Time")}}</th>
          <th v-for="day in days" :key="day" class="day-header">{{ $t(day) }}</th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="time in times" :key="time">
          <td class="time-cell">{{ time }}</td>
          <td v-for="day in days" :key="day" class="lesson-cell">
            <div class="lessons-list">
              <div
                v-for="(lesson, idx) in (grid[day] && grid[day][time] ? grid[day][time] : [null])"
                :key="idx"
                class="lesson-item"
                :class="{'lesson-placeholder': !lesson || !lesson.subject}"
                @click="lesson && onCardClick(lesson.id)"
              >
                <template v-if="lesson && lesson.subject">
                  <div class="lesson-header">
                    <span class="group-chip">{{ lesson.groupCode }}</span>
                    <span class="lang-chip">{{ (lesson.language || '').toUpperCase() }}</span>
                  </div>
                  <div class="lesson-body">
                    <p class="subject">
                      {{ lesson.subject === 'null' ? $t("No Subject") : lesson.subject }}
                    </p>
                    <p class="meta">
                      <span>
                        {{ !lesson.teacher || lesson.teacher === 'null' ? $t("No Teacher") : lesson.teacher }}
                      </span>
                      <span>
                        {{ !lesson.room || lesson.room === 'null' ? $t('No Room') : lesson.room }}
                      </span>
                    </p>
                  </div>
                </template>
              </div>
            </div>
          </td>
        </tr>
      </tbody>
    </table>

    <PopupModal v-model="showModal" :title="$t('Edit Lesson')" width="500px">
      <template #default>
        <div class="modal-body">
          <label for="day">{{$t("Day")}}</label>
          <input id="day" v-model="edited.day" type="text" />
          
          <label for="time">{{ $t("Time") }}</label>
          <input id="time" v-model="edited.time" type="text" />

          <label for="groupCode">{{ $t("Group name") }}</label>
          <input id="groupCode" v-model="edited.groupCode" type="text" />

          <v-select
            v-model="edited.language"
            :items="[
              { text: 'uz', value: 'uz' },
              { text: 'rus', value: 'rus' }
            ]"
            :label="$t('education_language_name')"
            outlined
            dense
            hide-details
            class="migalka-select mt-2 mb-3"
          />

          <label for="subject">{{$t("Lesson name")}}</label>
          <input id="subject" v-model="edited.subject" type="text" />

          <label for="teacher">{{$t("Teacher")}}</label>
          <input id="teacher" v-model="edited.teacher" type="text" />

          <label for="room">{{$t("Room")}}</label>
          <input id="room" v-model="edited.room" type="text" />

          <v-select
            v-model="edited.lessonType"
            :items="[
              { text: $t('AMALIY'), value: 'AMALIY' },
              { text: $t('LEKSIYA'), value: 'LEKSIYA' }
            ]"
            :label="$t('Lesson Type')"
            outlined
            dense
            hide-details
            class="migalka-select mt-2"
          />

          <v-select
            v-model="edited.migalka"
            :items="migalkaItems"
            :label="$t('Migalka')"
            outlined
            dense
            hide-details
            class="migalka-select mt-5"
          />
        </div>
      </template>
      <template #footer>
        <button class="cancel-btn" @click="showModal = false">{{$t("Cancel")}}</button>
        <button class="save-btn" @click="saveLesson">{{$t("Save")}}</button>
      </template>
    </PopupModal>
  </div>
</template>

<script>
import axios from 'axios';
import PopupModal from '@/components/lesson/PopupModal.vue';

// simple debounce helper (no external deps)
function debounce(fn, delay = 400) {
  let t = null;
  return function (...args) {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), delay);
  };
}

export default {
  name: 'LessonSchedulePage',
  components: { PopupModal },
  data() {
    return {
      lessons: [],
      loading: false,
      showModal: false,

      // Comprehensive filters
      filters: {
        groupCode: '',   // server-side fetch when set
        day: null,       // DUSHANBA | ...
        time: null,      // e.g. "08:00-09:30"
        language: null,  // 'uz' | 'rus'
        lessonType: null,// 'AMALIY' | 'LEKSIYA'
        migalka: null    // true | false
      },

      edited: {
        id: null,
        day: '',
        time: '',
        groupCode: '',
        language: '',
        subject: '',
        teacher: '',
        room: '',
        lessonType: null,
        migalka: null
      }
    };
  },
  computed: {
    // Constants
    days() {
      return ['DUSHANBA', 'SESHANBA', 'CHORSHANBA', 'PAYSHANBA', 'JUMA', 'SHANBA'];
    },
    languageItems() {
      return [
        { text: 'uz',  value: 'uz' },
        { text: 'rus', value: 'rus' }
      ];
    },
    lessonTypeItems() {
      return [
        { text: this.$t('AMALIY'), value: 'AMALIY' },
        { text: this.$t('LEKSIYA'), value: 'LEKSIYA' }
      ];
    },
    migalkaItems() {
      return [
        { text: this.$t('MIGALKALI'),  value: true  },
        { text: this.$t('MIGALKASIZ'), value: false }
      ];
    },

    uploadId() {
      return this.$route.params.uploadId;
    },

    // all times from the full dataset (for the Time dropdown)
    timesAll() {
      const s = new Set(this.lessons.map(l => l.time).filter(Boolean));
      return Array.from(s).sort();
    },

    // Apply client-side filters (except groupCode which is server-side)
    filteredLessons() {
      return this.lessons.filter(l => {
        if (this.filters.day && l.day !== this.filters.day) return false;
        if (this.filters.time && l.time !== this.filters.time) return false;
        if (this.filters.language && l.language !== this.filters.language) return false;
        if (this.filters.lessonType && l.lessonType !== this.filters.lessonType) return false;
        if (typeof this.filters.migalka === 'boolean' && l.migalka !== this.filters.migalka) return false;
        return true;
      });
    },

    // Times to render rows for the current (filtered) view
    times() {
      const s = new Set(this.filteredLessons.map(l => l.time).filter(Boolean));
      return Array.from(s).sort();
    },

    // Grid built from filtered lessons
    grid() {
      const g = {};
      this.filteredLessons.forEach(l => {
        if (!g[l.day]) g[l.day] = {};
        if (!g[l.day][l.time]) g[l.day][l.time] = [];
        g[l.day][l.time].push(l);
      });
      return g;
    },

    hasAnyFilter() {
      const { groupCode, day, time, language, lessonType, migalka } = this.filters;
      return !!(groupCode || day || time || language || lessonType || typeof migalka === 'boolean');
    }
  },
  watch: {
    // Debounced server fetch on groupCode changes
    'filters.groupCode': {
      handler: debounce(function () {
        // if user clears it, fall back to full list
        this.fetchLessons();
      }, 400),
      immediate: false
    }
  },
  mounted() {
    this.fetchLessons();
    if (typeof this.edited.migalka !== 'boolean') {
      this.$set(this.edited, 'migalka', !!this.edited.migalka);
    }
  },
  methods: {
    async fetchLessons() {
      this.loading = true;
      try {
        if (this.filters.groupCode) {
          const { data } = await axios.get('/lessons/get/lesson/by-group', {
            params: {
              groupCode: this.filters.groupCode,
              uploadId: this.uploadId
            }
          });
          this.lessons = Array.isArray(data) ? data : [];
        } else {
          const { data } = await axios.get(`lessons/uploads/${this.uploadId}/items`);
          this.lessons = Array.isArray(data) ? data : [];
        }
      } catch (err) {
        console.error('Failed to load lessons', err);
        this.lessons = [];
      } finally {
        this.loading = false;
      }
    },

    resetFilters() {
      this.filters = {
        groupCode: '',
        day: null,
        time: null,
        language: null,
        lessonType: null,
        migalka: null
      };
      this.fetchLessons();
    },

    async onCardClick(id) {
      this.showModal = true;
      try {
        const { data } = await axios.get(`lessons/${id}`);
        this.edited = { id, ...data };
      } catch (err) {
        console.error('Failed to fetch lesson details', err);
      }
    },

    async saveLesson() {
      try {
        const { data } = await axios.put(`lessons/${this.edited.id}`, this.edited);
        const idx = this.lessons.findIndex(l => l.id === data.id);
        if (idx !== -1) this.$set(this.lessons, idx, data);
      } catch (err) {
        console.error('Failed to save lesson', err);
      } finally {
        this.showModal = false;
      }
    }
  }
};
</script>

<style scoped>
.schedule-container {
  padding: 24px;
  width: 100%;
  overflow-x: auto;
  overflow-y: hidden;
}

.page-title {
  font-size: 1.75rem;
  font-weight: 600;
  margin-bottom: 16px;
  color: #1f2937;
}

/* Modern filters bar */
.filters-bar {
  display: grid;
  grid-template-columns: repeat(6, minmax(160px, 1fr)) auto;
  grid-gap: 12px;
  align-items: end;
  margin-bottom: 12px;
}
.filter-item {
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.filter-item > label {
  font-size: 0.8rem;
  color: #52525b;
}
.filter-item > input {
  height: 36px;
  border-radius: 10px;
  border: 1px solid #e5e7eb;
  padding: 0 10px;
  outline: none;
  transition: box-shadow .2s, border-color .2s;
}
.filter-item > input:focus {
  border-color: #00274c;
  border-width: 2px;
}

.filter-actions {
  display: flex;
  gap: 8px;
  justify-content: flex-end;
}
.btn {
  height: 36px;
  border-radius: 10px;
  padding: 0 14px;
  border: 1px solid #e5e7eb;
  background: #111827;
  color: #fff;
  cursor: pointer;
  transition: opacity .2s, box-shadow .2s, transform .02s;
}
.btn:hover { opacity: .95; }
.btn:active { transform: translateY(1px); }
.btn.ghost {
  background: #fff;
  color: #111827;
}

.loader, .no-data {
  text-align: center;
  color: #6b7280;
  margin: 24px 0;
}
.schedule-table {
  width: 200%;
  border-collapse: collapse;
}
.schedule-table th,
.schedule-table td {
  border: 1px solid #e5e7eb;
  vertical-align: top;
  padding: 8px;
}
.time-header,
.time-cell {
  background: #f9fafb;
  font-weight: 600;
  text-align: center;
  width: 160px;
}
.day-header {
  background: #f3f4f6;
  font-weight: 600;
  text-align: center;
}
.lesson-cell {
  background: #fff;
  width: 750px;
}
.time-cell {
  width: 250px;
}
.lessons-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.lesson-item {
  background: #f3f4f6;
  border-radius: 8px;
  padding: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  cursor: pointer;
  min-height: 120px;
}
.lesson-item:hover {
  background: #eef2ff;
}
.lesson-placeholder {
  visibility: hidden;
}
.lesson-header {
  display: flex;
  justify-content: space-between;
  margin-bottom: 4px;
}
.group-chip,
.lang-chip {
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 0.75rem;
  font-weight: 600;
  color: #fff;
}
.group-chip { background: #4f46e5; }
.lang-chip  { background: #10b981; }
.subject {
  font-size: 0.875rem;
  font-weight: 500;
  margin: 0;
}
.meta {
  font-size: 0.75rem;
  color: #6b7280;
  display: flex;
  justify-content: space-between;
}

/* keep your existing select polish */
.migalka-select:hover { cursor: pointer !important; }
.migalka-select ::v-deep .v-input__slot {
  background-color: #ffffff;
  border-radius: 10px;
  border: 1px solid #e0e0e0;
  transition: all 0.2s ease;
}
.migalka-select ::v-deep .v-input__slot:hover {
  border-color: #c5a46d;
  box-shadow: 0 4px 12px rgba(197, 164, 109, 0.15);
}
.migalka-select ::v-deep .v-label {
  font-weight: 500;
  color: #444;
}
.migalka-select ::v-deep .v-select__selections {
  color: #333;
  font-weight: 500;
}

/* ══════════════════════════════════════════════════════════════
   APPLE FONT STACK
   ══════════════════════════════════════════════════════════════ */
.schedule-container {
  font-family: -apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", "Helvetica Neue", Helvetica, Arial, sans-serif;
}

/* ══════════════════════════════════════════════════════════════
   DARK MODE
   ══════════════════════════════════════════════════════════════ */

/* ── Page title ── */
.theme--dark .page-title {
  color: #e4e5eb;
}

/* ── Filters bar ── */
.theme--dark .filter-item > label {
  color: #8b8d9e;
}
.theme--dark .filter-item > input {
  background: #22232e;
  border-color: #2a2c3a;
  color: #d5d6de;
}
.theme--dark .filter-item > input::placeholder {
  color: #606275;
}
.theme--dark .filter-item > input:focus {
  border-color: #ffd451;
  box-shadow: 0 4px 12px rgba(255, 212, 81, 0.1);
}

/* ── Filter buttons ── */
.theme--dark .btn {
  background: #ffd451;
  color: #111318;
  border-color: #ffd451;
}
.theme--dark .btn.ghost {
  background: #22232e;
  color: #d5d6de;
  border-color: #2a2c3a;
}
.theme--dark .btn.ghost:hover {
  background: #33354a;
}

/* ── Loader / No data ── */
.theme--dark .loader,
.theme--dark .no-data {
  color: #8b8d9e;
}

/* ── Schedule table ── */
.theme--dark .schedule-table th,
.theme--dark .schedule-table td {
  border-color: #2a2c3a;
}
.theme--dark .time-header,
.theme--dark .time-cell {
  background: #1a1b23;
  color: #8b8d9e;
}
.theme--dark .day-header {
  background: #1a1b23;
  color: #8b8d9e;
}
.theme--dark .lesson-cell {
  background: #111318;
}

/* ── Lesson cards ── */
.theme--dark .lesson-item {
  background: #1a1b23;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
}
.theme--dark .lesson-item:hover {
  background: #22232e;
}
.theme--dark .subject {
  color: #d5d6de;
}
.theme--dark .meta {
  color: #8b8d9e;
}

/* ── Vuetify selects in filters ── */
.theme--dark .migalka-select ::v-deep .v-input__slot {
  background-color: #22232e !important;
  border-color: #2a2c3a !important;
}
.theme--dark .migalka-select ::v-deep .v-input__slot:hover {
  border-color: #ffd451 !important;
  box-shadow: 0 4px 12px rgba(255, 212, 81, 0.1) !important;
}
.theme--dark .migalka-select ::v-deep .v-label {
  color: #8b8d9e !important;
}
.theme--dark .migalka-select ::v-deep .v-select__selections {
  color: #d5d6de !important;
}

/* ── Modal body inputs (edit lesson) ── */
.theme--dark .modal-body label {
  color: #8b8d9e;
}
.theme--dark .modal-body input {
  background: #22232e;
  border-color: #2a2c3a;
  color: #d5d6de;
}
.theme--dark .modal-body input:focus {
  border-color: #ffd451;
}
.theme--dark .cancel-btn {
  background: #22232e;
  color: #d5d6de;
}
.theme--dark .cancel-btn:hover {
  background: #33354a;
}
</style>
